import { useMemo, useRef, useEffect } from "react";
import { CircularProgress, Typography } from "@material-ui/core";
import { i18n } from "../translate/i18n";

const polyfillApexHasOwnProperty = () => {
  const globalScope = typeof window !== "undefined" ? window : null;

  if (!globalScope || typeof Object.create !== "function" || globalScope.__hasOwnPropertyPolyfilled) {
    return;
  }

  globalScope.__hasOwnPropertyPolyfilled = true;
  const origCreate = Object.create;

  Object.create = function (proto, propertiesObject) {
    if (proto === null) {
      const obj = origCreate(null, propertiesObject);
      Object.defineProperty(obj, "hasOwnProperty", {
        value: Object.prototype.hasOwnProperty,
        configurable: true,
        writable: true,
        enumerable: false
      });
      return obj;
    }

    return origCreate(proto, propertiesObject);
  };
};

polyfillApexHasOwnProperty();

export const useMountedState = () => {
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted;
};

export const useAbortController = () => {
  const controller = useRef(new AbortController());

  useEffect(() => {
    const currentController = controller.current;
    return () => {
      currentController.abort();
    };
  }, []);

  return controller.current;
};

export const getChartColors = (theme, type = "default") => {
  const colors = {
    default: [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.success.main,
      "#9c27b0",
      "#795548",
      "#607d8b",
      "#f50057",
    ],
    donut: [
      theme.palette.primary.main,
      theme.palette.info.main,
      theme.palette.error.main,
      theme.palette.warning.main,
      theme.palette.success.main,
      "#4B4B4B",
    ],
    bar: [theme.palette.primary.main, theme.palette.primary.light],
    line: [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
    ],
    multiline: [theme.palette.primary.main, theme.palette.success.main],
  };

  return colors[type] || colors.default;
};

export const getBaseChartOptions = (theme) => ({
  chart: {
    fontFamily: theme.typography.fontFamily,
    background: "transparent",
    foreColor: theme.palette.text.primary,
    animations: {
      enabled: true,
      easing: "easeinout",
      speed: 350,
      dynamicAnimation: {
        enabled: true,
        speed: 350,
      },
    },
    toolbar: {
      show: false,
    },
  },
  tooltip: {
    theme: theme.palette.type,
    style: {
      fontSize: "12px",
      fontFamily: theme.typography.fontFamily,
    },
    marker: {
      show: true,
    },
  },
  grid: {
    borderColor: theme.palette.divider,
    strokeDashArray: 4,
  },
  legend: {
    fontSize: "12px",
    fontFamily: theme.typography.fontFamily,
    labels: {
      colors: theme.palette.text.primary,
    },
    markers: {
      width: 10,
      height: 10,
      radius: 5,
    },
    itemMargin: {
      horizontal: 8,
      vertical: 3,
    },
  },
});

export const useBarChartOptions = (theme, categories, overrides = {}) => {
  return useMemo(() => {
    const baseOptions = getBaseChartOptions(theme);

    return {
      ...baseOptions,
      chart: {
        ...baseOptions.chart,
        type: "bar",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "70%",
          distributed: false,
          ...(overrides.plotOptions?.bar || {}),
        },
      },
      dataLabels: {
        enabled: false,
        ...(overrides.dataLabels || {}),
      },
      xaxis: {
        categories,
        labels: {
          style: {
            fontSize: "10px",
            colors: theme.palette.text.secondary,
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        ...(overrides.xaxis || {}),
      },
      yaxis: {
        labels: {
          formatter: (val) => Math.round(val),
          style: {
            colors: theme.palette.text.secondary,
          },
        },
        ...(overrides.yaxis || {}),
      },
      colors: getChartColors(theme, "bar"),
      ...(overrides || {}),
    };
  }, [theme, categories, overrides]);
};

export const useDonutChartOptions = (theme, labels, overrides = {}) => {
  return useMemo(() => {
    const baseOptions = getBaseChartOptions(theme);

    return {
      ...baseOptions,
      chart: {
        ...baseOptions.chart,
        type: "donut",
      },
      labels,
      colors: getChartColors(theme, "donut"),
      plotOptions: {
        pie: {
          donut: {
            size: "60%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "12px",
                fontWeight: 600,
              },
              value: {
                show: true,
                fontSize: "14px",
                fontWeight: 400,
              },
              total: {
                show: true,
                label: "Total",
                fontSize: "14px",
                fontWeight: 600,
                formatter: (w) => {
                  return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                },
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Math.round(val)}%`,
        style: {
          fontSize: "12px",
          fontWeight: 500,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      ...(overrides || {}),
    };
  }, [theme, labels, overrides]);
};

export const useLineChartOptions = (theme, categories, overrides = {}) => {
  return useMemo(() => {
    const baseOptions = getBaseChartOptions(theme);

    return {
      ...baseOptions,
      chart: {
        ...baseOptions.chart,
        type: "line",
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      markers: {
        size: 0,
        hover: {
          size: 5,
          sizeOffset: 3,
        },
      },
      xaxis: {
        categories,
        labels: {
          style: {
            fontSize: "10px",
            colors: theme.palette.text.secondary,
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        ...(overrides.xaxis || {}),
      },
      yaxis: {
        labels: {
          formatter: (val) => Math.round(val),
          style: {
            colors: theme.palette.text.secondary,
          },
        },
        ...(overrides.yaxis || {}),
      },
      colors: getChartColors(theme, "line"),
      ...(overrides || {}),
    };
  }, [theme, categories, overrides]);
};

export const ChartPlaceholders = {
  Loading: ({ classes }) => (
    <div className={classes.loadingContainer}>
      <CircularProgress size={40} />
    </div>
  ),

  NoData: ({ classes }) => (
    <div className={classes.noDataContainer}>
      <Typography className={classes.noDataText}>
        {i18n.t("dashboard.noData") || "Sem dados disponíveis"}
      </Typography>
    </div>
  ),
};
